/**
 * Gegenüberstellung alter und neuer Fassung eines Abschnitts: erst zeilenweise (Absätze, Aufzählungen),
 * dann in geänderten Zeilen wortweise. Ein Abschnitt hat bis zu einigen Hundert Zeilen — eine
 * Wort-Diff über den ganzen Text wäre zu teuer, eine nur zeilenweise zu grob.
 */

/**
 * Eine Zeile auf ihren Inhalt zurückgeführt — wie `canonicalMarkdown` auf dem Server: Maskierung,
 * `&amp;`, aufgefüllte Tabellenzellen, Trennzeile und Aufzählungszeichen, die der Editor beim
 * Speichern nur anders schreibt, sind keine Änderung.
 */
export const canonicalLine = (line: string): string =>
  line
    .replace(/<div data-page-break="\d+"><\/div>/g, "")
    .replace(/\u200b/g, "")
    .replace(/^(\s*)[*+](\s)/, "$1-$2")
    .replace(/\\([\\`*_{}[\]()#+\-.!|&<>~])/g, "$1")
    .replace(/&amp;/g, "&")
    .replace(/\|\s*:?-{2,}:?\s*(?=\|)/g, "|---")
    .replace(/\s+/g, " ")
    .trim();

export interface DiffPart {
  kind: "same" | "added" | "removed";
  text: string;
}

/** Längste gemeinsame Teilfolge als Liste von Operationen. */
const lcsOps = <T>(a: T[], b: T[], eq: (x: T, y: T) => boolean): Array<["same" | "added" | "removed", T]> => {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = eq(a[i], b[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out: Array<["same" | "added" | "removed", T]> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (eq(a[i], b[j])) {
      out.push(["same", a[i]]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) out.push(["removed", a[i++]]);
    else out.push(["added", b[j++]]);
  }
  while (i < n) out.push(["removed", a[i++]]);
  while (j < m) out.push(["added", b[j++]]);
  return out;
};

const merge = (parts: DiffPart[]): DiffPart[] =>
  parts.reduce<DiffPart[]>((acc, p) => {
    const last = acc[acc.length - 1];
    if (last && last.kind === p.kind) last.text += p.text;
    else if (p.text) acc.push({ ...p });
    return acc;
  }, []);

const words = (line: string) => line.match(/\s+|[^\s]+/g) ?? [];

/** Wortweise Diff zweier Zeilen (Leerraum zählt als eigenes Token). */
export const wordDiff = (a: string, b: string): DiffPart[] =>
  merge(
    lcsOps(words(a), words(b), (x, y) => x === y || (!x.trim() && !y.trim()) || canonicalLine(x) === canonicalLine(y)).map(([kind, text]) => ({ kind, text })),
  );

/** Gemeinsamer Wortanteil mindestens 40 % — dann ist es dieselbe Zeile, geändert. */
const similar = (a: string, b: string): boolean => {
  const wa = a.split(/\s+/).filter(Boolean);
  const wb = b.split(/\s+/).filter(Boolean);
  if (!wa.length || !wb.length) return false;
  const common = lcsOps(wa, wb, (x, y) => x === y).filter(([kind]) => kind === "same").length;
  return common / Math.max(wa.length, wb.length) >= 0.4;
};

/**
 * Diff zweier Texte. Ein Block entfernter Zeilen direkt vor einem Block hinzugefügter gilt Zeile für
 * Zeile als geändert und wird wortweise verglichen; Zeilen enden mit ihrem Umbruch.
 */
export const textDiff = (oldText: string, newText: string): DiffPart[] => {
  const split = (t: string) => t.split(/(?<=\n)/);
  const ops = lcsOps(split(oldText), split(newText), (x, y) => canonicalLine(x) === canonicalLine(y));
  const out: DiffPart[] = [];
  let k = 0;
  while (k < ops.length) {
    if (ops[k][0] === "same") {
      out.push({ kind: "same", text: ops[k++][1] });
      continue;
    }
    const removed: string[] = [];
    const added: string[] = [];
    while (k < ops.length && ops[k][0] === "removed") removed.push(ops[k++][1]);
    while (k < ops.length && ops[k][0] === "added") added.push(ops[k++][1]);
    const pairs = Math.min(removed.length, added.length);
    for (let i = 0; i < pairs; i++) {
      // Nur ähnliche Zeilen wortweise — zwei ganz verschiedene Sätze wären sonst Wortsalat.
      if (similar(removed[i], added[i])) out.push(...wordDiff(removed[i], added[i]));
      else out.push({ kind: "removed", text: removed[i] }, { kind: "added", text: added[i] });
    }
    for (const line of removed.slice(pairs)) out.push({ kind: "removed", text: line });
    for (const line of added.slice(pairs)) out.push({ kind: "added", text: line });
  }
  return merge(out);
};
